import { api } from '../client';

/**
 * Where the lead time goes, and what the freight costs against the goods.
 *
 * Every figure here is nullable on purpose. Each has a plausible wrong answer
 * that is a number — a lead time of zero, a hot-shot ratio of 0%, a freight
 * ratio against goods nobody priced — and each of those reads as a measurement
 * while being an absence.
 */

export interface LeadTimeStage {
  /** Null below a usable sample. `observations` says how thin it was. */
  days: number | null;
  observations: number;
}

export interface SupplierLeadTime {
  supplierId: string;
  supplier: string;
  /** Ordered → despatched: the supplier making it. */
  manufacturing: LeadTimeStage;
  /** Despatched → arrived: the freight moving it. */
  transit: LeadTimeStage;
  /** Arrived → received: clearing it and putting it away. */
  clearance: LeadTimeStage;
  /** Only where all three stages were measurable. */
  totalDays: number | null;
  /** The stage worth arguing about. */
  dominantStage: 'manufacturing' | 'transit' | 'clearance' | null;
}

export interface ExpediteRatio {
  shipments: number;
  expedited: number;
  /** Null when nothing moved at all. */
  countRatio: number | null;
  /** Null while no shipment carries a cost — unknown, not nil. */
  spendRatio: number | null;
  pricedShipments: number;
  reasons: { reason: string; count: number }[];
}

export interface FreightToValueLine {
  itemId: string;
  sku: string;
  name: string;
  freight: number;
  value: number;
  units: number;
  unpriced: boolean;
  /** Null where the goods have no cost recorded. */
  ratio: number | null;
}

export interface FreightToValue {
  basis: string;
  /** Why the basis is what it is. Shown, not buried. */
  basisNote: string;
  shipmentsPriced: number;
  lines: FreightToValueLine[];
}

export interface ItemExternalRef {
  id: string;
  system: string;
  reference: string;
  ownerCompanyId: string | null;
  /** Null means it is our own system. */
  ownerCompany: { id: string; displayName: string } | null;
}

export const supplyAnalyticsApi = {
  leadTimeStages: async (days = 365): Promise<SupplierLeadTime[]> =>
    (await api.get('/supply-analytics/lead-time-stages', { params: { days } })).data,

  expedite: async (days = 90): Promise<ExpediteRatio> =>
    (await api.get('/supply-analytics/expedite', { params: { days } })).data,

  freightToValue: async (days = 180): Promise<FreightToValue> =>
    (await api.get('/supply-analytics/freight-to-value', { params: { days } })).data,

  refs: async (itemId: string): Promise<ItemExternalRef[]> =>
    (await api.get(`/supply-analytics/items/${itemId}/refs`)).data,

  setRef: async (
    itemId: string,
    dto: { system: string; reference: string; ownerCompanyId?: string }
  ): Promise<ItemExternalRef> =>
    (await api.post(`/supply-analytics/items/${itemId}/refs`, dto)).data,

  removeRef: async (itemId: string, refId: string) =>
    (await api.delete(`/supply-analytics/items/${itemId}/refs/${refId}`)).data,

  /** Find our item from somebody else's number — the point of recording them. */
  lookup: async (system: string, reference: string) =>
    (await api.get('/supply-analytics/items/lookup', { params: { system, reference } })).data,

  setFreight: async (
    shipmentId: string,
    dto: { freightCost?: number | null; isExpedited?: boolean; expediteReason?: string }
  ) => (await api.patch(`/supply-analytics/shipments/${shipmentId}/freight`, dto)).data,
};
